import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlagService } from '@vendasta/businesses';
import { ChangeEmailDomainStatus, DomainService, EmailService } from '@vendasta/email';
import { oneResultFrom } from '@vendasta/rx-utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PartnerService } from '../core/partner.service';
import { VConfigService } from '../vconfig/vconfig.service';
import { PendingDomainService } from './pending-domain/pending-domain.service';
import { SPFService } from './shared/spf.service';

const UPDATE_SEND_AS_INFORMATION_URL = '/api/v3/partner/updateSendAsInformation/';
const GET_SEND_AS_INFORMATION_URL = '/api/v3/partner/getSendAsInformation/';
const EMAIL_SETTINGS_LEARN_MORE_URL = 'https://support.vendasta.com/hc/en-us/articles/4406961173655';

@Injectable({ providedIn: 'root' })
export class CampaignSettingsService {
  private domain$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public domain$: Observable<string> = this.domain$$.asObservable();
  private senderEmail$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public senderEmail$: Observable<string> = this.senderEmail$$.asObservable();
  public dkim_application_id = 'marketing';

  public pendingDomainFeatureEnabled$: Observable<boolean>;

  constructor(
    private vconfigService: VConfigService,
    private router: Router,
    private http: HttpClient,
    private emailService: EmailService,
    private partnerService: PartnerService,
    private domainService: DomainService,
    private featureFlagService: FeatureFlagService,
    private pendingDomainService: PendingDomainService,
    private spfService: SPFService,
  ) {
    this.pendingDomainFeatureEnabled$ = this.featureFlagService.checkFeatureFlag(
      this.partnerService.partnerId,
      '',
      'pending_email_domain',
    );
  }

  public getSendAsInformation(partnerId: string): Observable<GetEmailSendAsResponse> {
    return this.http
      .get(GET_SEND_AS_INFORMATION_URL, {
        withCredentials: true,
        params: {
          partnerId: partnerId,
        },
      })
      .pipe(
        map((response: any) => {
          return {
            sendAsName: response.data.sendAsName,
            sendAsEmailAddress: response.data.sendAsEmailAddress,
            replyToEmailAddress: response.data.replyToEmailAddress,
          };
        }),
      );
  }

  public createDkim(domain: string): Observable<HttpResponse<null>> {
    return this.emailService.createEmailDomain(this.partnerService.partnerId, this.dkim_application_id, '', domain);
  }

  public buildSpf(directive: string): string {
    return this.spfService.buildSpfRecord('', directive);
  }

  public updateSendAsInformation(SendAsInformation: EmailSendAsRequest): Observable<EmailSendAsResponse> {
    return this.http.post(UPDATE_SEND_AS_INFORMATION_URL, SendAsInformation, { withCredentials: true }).pipe(
      map((response: any) => {
        return {
          message: response.data,
        };
      }),
    );
  }

  public setDomain(domain: string): void {
    this.domain$$.next(domain);
  }

  public setSenderEmail(address: string): void {
    this.senderEmail$$.next(address);
  }

  public openEmailSettingsLearnMore(): void {
    window.open(EMAIL_SETTINGS_LEARN_MORE_URL, '_blank', 'noopener');
  }

  public async requestDomainChange(username: string, domain: string): Promise<ChangeEmailDomainStatus> {
    const partnerId = this.partnerService.partnerId;
    const change$ = this.domainService.changeForPartner(partnerId, domain, username);
    return oneResultFrom(change$);
  }

  public refreshPendingDomain(): void {
    this.pendingDomainService.refresh();
  }
}

export interface EmailSendAsRequest {
  sendAsEmailAddress: string;
  replyToEmailAddress: string;
  sendAsName: string;
  partnerId: string;
}

export interface EmailSendAsResponse {
  message: string;
}

export interface GetEmailSendAsResponse {
  sendAsEmailAddress: string;
  replyToEmailAddress: string;
  sendAsName: string;
}
